import React, { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { sr } from '../translations/sr';
import { en } from '../translations/en';
import { dashboardApi } from '../services/api';
import toast from 'react-hot-toast';

// Available languages
const translations: Record<string, Record<string, string>> = {
  sr,
  en,
};

// Supported language codes
const SUPPORTED_LANGUAGES = ['sr', 'en'];

// Get browser language preference
const getBrowserLanguage = (): string => {
  if (typeof window === 'undefined') return 'sr';
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : 'sr';
};

// Get language from localStorage
const getStoredLanguage = (): string | null => {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('user_language');
  return stored && SUPPORTED_LANGUAGES.includes(stored) ? stored : null;
};

// Define the translation function type
type TranslationValues = Record<string, string | number | undefined | null>;
type TranslationFunction = (key: string, values?: TranslationValues) => string;

// Define the context value type
interface TranslationContextType {
  t: TranslationFunction;
  language: string;
  translations: Record<string, string>;
  changeLanguage: (lang: string) => Promise<void>;
  isLoading: boolean;
}

// Create the context with proper typing
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Define the hook return type
export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Define the provider props type
interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  
  // Track authentication state reactively
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return !!localStorage.getItem('auth_token');
  });

  // Listen for auth token changes
  useEffect(() => {
    const checkAuthStatus = () => {
      if (typeof window === 'undefined') return false;
      return !!localStorage.getItem('auth_token');
    };

    // Check auth on mount
    setIsAuthenticated(checkAuthStatus());

    // Listen for storage events (e.g., when user logs in/out in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' || e.key === null) {
        // null means all keys were cleared (logout)
        setIsAuthenticated(checkAuthStatus());
      }
    };

    // Custom event listener for same-tab auth changes
    const handleAuthChange = () => {
      setIsAuthenticated(checkAuthStatus());
    };

    window.addEventListener('storage', handleStorageChange);
    // Listen for custom auth events (can be dispatched by AuthContext)
    window.addEventListener('auth-changed', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-changed', handleAuthChange);
    };
  }, []);

  // Check if user is authenticated
  const checkAuth = useCallback(() => {
    return isAuthenticated;
  }, [isAuthenticated]);

  // Initialize language state - try stored, then browser, then default to 'sr'
  const [language, setLanguage] = useState<string>(() => {
    return getStoredLanguage() || getBrowserLanguage();
  });

  // Get current translations based on language
  const currentTranslations = translations[language] || sr;

  // Fetch user preferences from backend
  const { data: preferencesData, isLoading: isLoadingPreferences } = useQuery(
    'dashboardPreferences',
    () => dashboardApi.getPreferences(),
    {
      enabled: isAuthenticated,
      refetchOnWindowFocus: false,
      retry: 1,
      onSuccess: (data) => {
        // Backend returns: { success: true, data: { config: {...}, language: 'sr' } }
        const userLanguage = data?.data?.language;
        if (userLanguage && SUPPORTED_LANGUAGES.includes(userLanguage)) {
          setLanguage(userLanguage);
          localStorage.setItem('user_language', userLanguage);
        }
      },
      onError: () => {
        // On error, use stored language or browser language
        const storedLang = getStoredLanguage();
        if (storedLang) {
          setLanguage(storedLang);
        }
      },
    }
  );

  // Mutation to update language preference
  const updateLanguageMutation = useMutation(
    async (newLanguage: string) => {
      return await dashboardApi.updatePreferences({ language: newLanguage });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dashboardPreferences');
      },
      onError: (error: any) => {
        console.error('Failed to update language preference:', error);
        toast.error(currentTranslations.failedToUpdate || 'Failed to update language preference');
      },
    }
  );

  // Change language function
  const changeLanguage = useCallback(
    async (newLanguage: string) => {
      if (!SUPPORTED_LANGUAGES.includes(newLanguage)) {
        console.warn(`Unsupported language: ${newLanguage}`);
        return;
      }

      // Update local state immediately for better UX
      setLanguage(newLanguage);
      localStorage.setItem('user_language', newLanguage);

      // Save to backend if authenticated
      if (checkAuth()) {
        try {
          await updateLanguageMutation.mutateAsync(newLanguage);
          toast.success(
            newLanguage === 'sr' 
              ? 'Jezik je uspešno promenjen' 
              : 'Language changed successfully'
          );
        } catch (error) {
          // If backend update fails, keep local change but show error
          console.error('Failed to save language preference to backend:', error);
        }
      }
    },
    [checkAuth, updateLanguageMutation, currentTranslations]
  );

  // Translation function
  const interpolate = useCallback((template: string, values?: TranslationValues) => {
    if (!values) {
      return template;
    }

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, token: string) => {
      const value = values[token];
      return value !== undefined && value !== null ? String(value) : '';
    });
  }, []);

  const t: TranslationFunction = useCallback(
    (key: string, values?: TranslationValues) => {
      const template = currentTranslations[key] || key;
      return interpolate(template, values);
    },
    [currentTranslations, interpolate]
  );

  const value: TranslationContextType = {
    t,
    language,
    translations: currentTranslations,
    changeLanguage,
    isLoading: isLoadingPreferences,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};
